import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
    ActionIcon,
    Badge,
    Group,
    Pagination,
    Select,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconRefresh } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';

import { GET_HISTORICAL_OVERRIDE_HISTORY } from '../../apollo/queries/getHistoricalOverrideHistory';
import { RETRY_FAILED_HISTORICAL_IMPORT } from '../../apollo/mutations/retryFailedHistoricalImport';
import { ImportType } from '../../types/import';

interface HistoryRow {
    id: string;
    fileName: string;
    status: 'Completed' | 'Failed' | 'Cancelled' | string;
    createdAt: string;
    scheduledFor: string | null;
    startedAt: string | null;
    completedAt: string | null;
    recordsProcessed: number | null;
    error: string | null;
    cancelledAt: string | null;
    createdBy?: { firstName: string; lastName: string } | null;
    cancelledBy?: { firstName: string; lastName: string } | null;
}

const statusColors: Record<string, string> = {
    Completed: 'green',
    Failed: 'red',
    Cancelled: 'gray',
};

interface Props {
    readonly onChanged?: () => void;
}

export function HistoricalOverrideHistoryTable({
    onChanged,
}: Props): JSX.Element {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data, loading, refetch } = useQuery(
        GET_HISTORICAL_OVERRIDE_HISTORY,
        {
            variables: {
                input: {
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                    importType: ImportType.DailySalesHistoricalManualOverride,
                    statuses: ['Completed', 'Failed', 'Cancelled'],
                },
            },
            fetchPolicy: 'cache-and-network',
        },
    );

    const [retryMutation] = useMutation(RETRY_FAILED_HISTORICAL_IMPORT);

    useEffect(() => {
        // Reset to page 1 if the page size changes — otherwise a user on page 8
        // could end up viewing an out-of-range offset after shrinking pageSize.
        setPage(1);
    }, [pageSize]);

    const rows: HistoryRow[] = data?.getManualImportLogs?.logs ?? [];
    const total: number = data?.getManualImportLogs?.total ?? 0;

    const handleRetry = (row: HistoryRow) => {
        modals.openConfirmModal({
            title: 'Retry failed historical import?',
            centered: true,
            children: (
                <Text size="sm">
                    A new scheduled import will be created for &quot;
                    {row.fileName}&quot; using the original file. The new run
                    will execute at 00:00 SAST tonight.
                </Text>
            ),
            labels: { confirm: 'Retry import', cancel: 'Close' },
            confirmProps: { color: 'blue', size: 'xs' },
            cancelProps: { size: 'xs' },
            groupProps: {
                gap: 'xs',
                justify: 'flex-end',
                wrap: 'nowrap',
            },
            onConfirm: async () => {
                try {
                    await retryMutation({ variables: { id: row.id } });
                    notifications.show({
                        title: 'Retry scheduled',
                        message: `"${row.fileName}" has been rescheduled for the next nightly window.`,
                        color: 'green',
                    });
                    await refetch();
                    onChanged?.();
                } catch (e) {
                    notifications.show({
                        title: 'Retry failed',
                        message:
                            e instanceof Error
                                ? e.message
                                : 'Unknown error while scheduling retry',
                        color: 'red',
                    });
                }
            },
        });
    };

    return (
        <Stack gap="xs">
            <DataTable<HistoryRow>
                withTableBorder
                striped
                verticalSpacing="xs"
                fz="sm"
                minHeight={160}
                noRecordsText="No historical imports yet."
                records={rows}
                idAccessor="id"
                fetching={loading && rows.length === 0}
                columns={[
                    {
                        accessor: 'fileName',
                        title: 'File',
                        render: row => (
                            <Text size="sm" fw={500}>
                                {row.fileName}
                            </Text>
                        ),
                    },
                    {
                        accessor: 'scheduledFor',
                        title: 'Scheduled',
                        render: row =>
                            row.scheduledFor
                                ? dayjs(row.scheduledFor).format('DD MMM YYYY')
                                : '—',
                    },
                    {
                        accessor: 'completedAt',
                        title: 'Finished',
                        render: row => {
                            const finishedAt =
                                row.completedAt ?? row.cancelledAt;
                            if (!finishedAt) return '—';
                            return dayjs(finishedAt).format(
                                'DD MMM YYYY HH:mm',
                            );
                        },
                    },
                    {
                        accessor: 'status',
                        title: 'Status',
                        render: row => (
                            <Badge
                                color={statusColors[row.status] || 'gray'}
                                variant="light"
                            >
                                {row.status}
                            </Badge>
                        ),
                    },
                    {
                        accessor: 'recordsProcessed',
                        title: 'Records',
                        render: row =>
                            row.recordsProcessed != null
                                ? row.recordsProcessed.toLocaleString()
                                : '—',
                    },
                    {
                        accessor: 'error',
                        title: 'Notes',
                        ellipsis: true,
                        width: 280,
                        render: row => {
                            if (row.status === 'Failed' && row.error) {
                                return (
                                    <Tooltip
                                        label={row.error}
                                        multiline
                                        w={400}
                                    >
                                        <Text
                                            size="xs"
                                            c="red"
                                            truncate="end"
                                            style={{ maxWidth: 280 }}
                                        >
                                            {row.error}
                                        </Text>
                                    </Tooltip>
                                );
                            }
                            if (row.status === 'Cancelled' && row.cancelledBy) {
                                return (
                                    <Text size="xs" c="dimmed">
                                        Cancelled by {row.cancelledBy.firstName}{' '}
                                        {row.cancelledBy.lastName}
                                    </Text>
                                );
                            }
                            return null;
                        },
                    },
                    {
                        accessor: 'actions',
                        title: '',
                        textAlign: 'right',
                        render: row =>
                            row.status === 'Failed' ? (
                                <Group gap={4} justify="flex-end">
                                    <Tooltip label="Retry import">
                                        <ActionIcon
                                            color="blue"
                                            variant="subtle"
                                            onClick={() => handleRetry(row)}
                                        >
                                            <IconRefresh size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            ) : null,
                    },
                ]}
            />
            <Group justify="space-between" py="xs">
                <Text size="xs" c="dimmed">
                    {total} {total === 1 ? 'import' : 'imports'} in history
                </Text>
                <Group gap="md">
                    <Select
                        data={['10', '20', '50']}
                        value={pageSize.toString()}
                        onChange={v => v && setPageSize(Number(v))}
                        size="xs"
                        style={{ width: 80 }}
                        allowDeselect={false}
                    />
                    <Pagination
                        size="sm"
                        total={Math.max(1, Math.ceil(total / pageSize))}
                        value={page}
                        onChange={setPage}
                    />
                </Group>
            </Group>
        </Stack>
    );
}
